/* B2B enquiry form.
 *
 * Builds a normalised lead payload on submit and hands it off in three ways, so
 * wiring the CRM later is a config change rather than a code change:
 *
 *   1. Fires a `frido:b2b-lead` CustomEvent on `document` (detail = payload).
 *   2. Pushes to window.dataLayer, if present.
 *   3. POSTs JSON to the endpoint in the section's "Lead endpoint URL" setting.
 *
 * WITHOUT an endpoint configured, a submission is NOT persisted anywhere. It is
 * logged to the console and the success state is shown, but the lead is lost on
 * page unload. Set the endpoint before this page takes real traffic.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* The thank-you page greets the visitor by name. It's a plain cached Shopify
     page with no per-visitor Liquid, so the name has to travel on the client:
     stashed here as it's typed, and also appended to the redirect URL. Either
     one alone is enough — the stash covers a stripped query string, the query
     string covers blocked storage. */
  var NAME_KEY = 'frido-b2b-lead-name';

  /* Stashes the name and returns it normalised, so callers that also need to put
     it on a URL don't repeat the tidy-up. */
  function rememberName(value) {
    var name = (value || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    try {
      if (name) window.sessionStorage.setItem(NAME_KEY, name);
      else window.sessionStorage.removeItem(NAME_KEY);
    } catch (error) {
      /* Private mode / blocked storage — the query string still carries it. */
    }
    return name;
  }

  function withName(url, name) {
    if (!name) return url;
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + 'name=' + encodeURIComponent(name);
  }

  /* How many digits the OTP has, and how long to wait for the SDK to report a
     result before assuming it never will. verify() is fire-and-forget — the
     outcome only ever arrives via the event callback — so without this watchdog
     a dropped or unrecognised event leaves the UI stuck on "Verifying…". */
  var OTP_LENGTH = 6;
  var OTP_VERIFY_TIMEOUT = 20000;

  /* Field-error helpers live at module scope because BOTH initForm and
     initOtpless need them. They used to be nested in initForm, which meant every
     call from the OTP code threw a ReferenceError inside the SDK's callback —
     silently killing the rest of the handler. */
  function fieldWrapOf(input) {
    return input && input.closest ? input.closest('.frido-b2b-form__field') : null;
  }

  function setFieldError(input, message) {
    var wrap = fieldWrapOf(input);
    if (!wrap) return;
    wrap.classList.toggle('frido-b2b-form__field--invalid', Boolean(message));
    var node = wrap.querySelector('[data-error-for="' + input.name + '"]');
    if (node) node.textContent = message || '';
  }

  function initForm(form) {
    if (form.dataset.b2bFormReady === 'true') return;
    form.dataset.b2bFormReady = 'true';

    var submit = form.querySelector('[data-b2b-submit]');
    var label = form.querySelector('[data-submit-label]');
    var status = form.querySelector('[data-b2b-status]');
    var originalLabel = label ? label.textContent.trim() : '';

    var fieldWrap = fieldWrapOf;
    var setError = setFieldError;

    function validate() {
      var problems = [];

      [['name', 'Please enter your name'],
       ['company', 'Please enter your company name']].forEach(function (pair) {
        var input = form.elements[pair[0]];
        if (!input) return;
        var ok = input.value.trim().length > 1;
        setError(input, ok ? '' : pair[1]);
        if (!ok) problems.push(input);
      });

      var email = form.elements.email;
      if (email) {
        var emailOk = EMAIL_RE.test(email.value.trim());
        setError(email, emailOk ? '' : 'Please enter a valid work email');
        if (!emailOk) problems.push(email);
      }

      var phone = form.elements.phone;
      if (phone) {
        var digits = phone.value.replace(/\D/g, '');
        var phoneOk = digits.length >= 7 && digits.length <= 15;
        setError(phone, phoneOk ? '' : 'Please enter a valid mobile number');
        if (!phoneOk) problems.push(phone);
      }

      return problems;
    }

    function buildPayload() {
      var el = form.elements;
      var digits = el.phone ? el.phone.value.replace(/\D/g, '') : '';
      var code = el.country_code ? el.country_code.value : '';

      return {
        name: el.name ? el.name.value.trim() : '',
        company: el.company ? el.company.value.trim() : '',
        email: el.email ? el.email.value.trim() : '',
        country_code: code,
        phone: digits,
        phone_e164: code + digits,
        quantity: el.quantity ? el.quantity.value : '',
        furniture_type: el.furniture_type ? el.furniture_type.value : '',
        product_interest: el.product_interest ? el.product_interest.value : '',
        phone_verified: el.phone_verified ? el.phone_verified.value === 'true' : false,
        otpless_token: el.otpless_token ? el.otpless_token.value : '',
        source: 'frido-b2b-workspace',
        page_url: window.location.href,
        submitted_at: new Date().toISOString()
      };
    }

    /* The rapi.myfrido.com proxy (which forwards to LeadSquared) expects its own
       field names, so translate the internal payload at the network boundary.
       Keeping the two shapes separate means the CustomEvent / dataLayer consumers
       still get the richer internal payload untouched.

       Note: `designation` is required by the API but the form doesn't collect it
       (it isn't in the Figma design). Sent empty until a field is added or the
       CRM is confirmed to accept a blank. `product_interest` — the specific
       product from a mini-PDP quote click — has no field in their schema; it's
       appended to product_category so the context isn't lost. */
    function buildCrmPayload(payload) {
      var category = payload.furniture_type || '';
      if (payload.product_interest) {
        category = category
          ? category + ' — ' + payload.product_interest
          : payload.product_interest;
      }

      return {
        name: payload.name,
        work_email: payload.email,
        designation: payload.designation || '',
        mobile: payload.phone,
        qty: payload.quantity,
        company_name: payload.company,
        product_category: category,
        currentURL: payload.page_url,
        /* Identifies which funnel the lead came from, so the CRM can attribute
           it without inferring from the URL. */
        source: 'b2b-workspace',
        /* Sent so rapi.myfrido.com can validate the OTPless token server-side
           (with the Client ID/Secret it holds) before trusting the phone. */
        otpless_token: payload.otpless_token
      };
    }

    function setStatus(message, kind) {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('frido-b2b-form__status--visible', Boolean(message));
      status.classList.toggle('frido-b2b-form__status--error', kind === 'error');
      status.classList.toggle('frido-b2b-form__status--success', kind === 'success');
    }

    function setBusy(busy) {
      if (submit) submit.disabled = busy;
      if (label) label.textContent = busy ? 'Sending…' : originalLabel;
    }

    /* With a thank-you URL configured the visitor is sent there; without one the
       form falls back to the inline success state, so the page still works if
       the setting is ever cleared. */
    function succeed() {
      var name = rememberName(form.elements.name ? form.elements.name.value : '');

      var thankYou = form.dataset.thankyou;
      if (thankYou) {
        window.location.assign(withName(thankYou, name));
        return;
      }

      form.classList.add('frido-b2b-form--sent');
      setStatus(form.dataset.success || 'Thanks — our team will reach out shortly.', 'success');
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      /* Honeypot: quietly pretend it worked rather than tell a bot it failed. */
      if (form.elements.website && form.elements.website.value) {
        succeed();
        return;
      }

      var problems = validate();
      if (problems.length) {
        setStatus('Please check the highlighted fields.', 'error');
        problems[0].focus();
        return;
      }

      /* Gate on phone verification when OTPless is configured. */
      if (form.dataset.otplessAppid
        && form.elements.phone_verified
        && form.elements.phone_verified.value !== 'true') {
        setError(form.elements.phone, 'Please verify your mobile number first');
        setStatus('Please verify your mobile number before submitting.', 'error');
        if (form.elements.phone) form.elements.phone.focus();
        return;
      }

      var payload = buildPayload();

      document.dispatchEvent(new CustomEvent('frido:b2b-lead', { detail: payload }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'frido_b2b_lead', lead: payload });
      }

      var endpoint = form.dataset.endpoint;
      if (!endpoint) {
        /* No CRM wired yet — surface the payload so the shape can be checked
           against LeadSquared's field mapping before go-live. */
        console.info('[frido-b2b] Lead captured but NOT sent — no endpoint configured.', payload);
        succeed();
        return;
      }

      setStatus('', null);
      setBusy(true);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildCrmPayload(payload))
      })
        .then(function (response) {
          if (response.ok) {
            succeed();
            return null;
          }
          /* Read the body before throwing — the proxy explains *why* it rejected
             the lead, and that detail is the difference between a five-minute
             fix and guessing. */
          return response.text().catch(function () { return ''; }).then(function (body) {
            var failure = new Error('HTTP ' + response.status);
            failure.status = response.status;
            failure.body = body;
            throw failure;
          });
        })
        .catch(function (error) {
          console.error(
            '[frido-b2b] Lead submission failed',
            error.status || '(network/CORS)',
            error.body || error.message,
            payload
          );

          /* A rate limit is temporary and self-clearing; telling someone to
             "try again" on a 429 just burns the limit further, and telling them
             to email support is wrong when waiting a minute fixes it. */
          if (error.status === 429) {
            setStatus('Too many attempts from this network. Please wait a minute and try again.', 'error');
          } else {
            setStatus('Something went wrong. Please try again, or email b2b@myfrido.com.', 'error');
          }
        })
        .finally(function () {
          setBusy(false);
        });
    });

    /* Clear a field's error as soon as the visitor starts fixing it. */
    form.addEventListener('input', function (event) {
      var target = event.target;
      if (target.name && fieldWrap(target)) setError(target, '');
      /* Capture the name as it's typed rather than only on submit — see NAME_KEY. */
      if (target.name === 'name') rememberName(target.value);
    });

    /* Browser autofill can populate the field without firing `input`. */
    if (form.elements.name && form.elements.name.value) {
      rememberName(form.elements.name.value);
    }

    enhanceSelects(form);
    initOtpless(form);
  }

  /* ---------------------------------------------------------------------------
   * Custom select (combobox).
   * Replaces the native <select> UI on [data-b2b-combo] fields with an animated
   * listbox that looks and behaves the same on mobile and desktop. The native
   * <select> stays in the DOM (visually hidden) so form submission, the payload
   * readers, and any change listeners keep working — this only swaps the UI.
   * ------------------------------------------------------------------------- */

  function enhanceSelects(form) {
    form.querySelectorAll('select[data-b2b-combo]').forEach(buildCombo);
  }

  function buildCombo(select) {
    if (select.dataset.b2bComboReady === 'true') return;
    /* Native <select> has no custom UI when JS is off — that's the graceful
       fallback, so only mark ready once we've actually built the combo. */
    select.dataset.b2bComboReady = 'true';

    var field = select.closest('.frido-b2b-form__field');
    var label = field ? field.querySelector('.frido-b2b-form__label') : null;
    var baseId = select.id || ('b2b-combo-' + Math.random().toString(36).slice(2, 8));

    var combo = document.createElement('div');
    combo.className = 'frido-b2b-combo';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'frido-b2b-form__select frido-b2b-combo__button';
    button.id = baseId + '-btn';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    var valueSpan = document.createElement('span');
    valueSpan.className = 'frido-b2b-combo__value';
    button.appendChild(valueSpan);

    /* A self-labelled select (aria-label, e.g. the country code) keeps its own
       name — don't hijack the field's visible label, which belongs to another
       control in the same field. Otherwise point the field label at the button
       (the native select is hidden) so it reads "<label> <value>". */
    var ariaLabel = select.getAttribute('aria-label');
    if (ariaLabel) {
      button.setAttribute('aria-label', ariaLabel);
    } else if (label) {
      if (!label.id) label.id = baseId + '-label';
      label.htmlFor = button.id;
      button.setAttribute('aria-labelledby', label.id + ' ' + button.id);
    }

    var panel = document.createElement('ul');
    panel.className = 'frido-b2b-combo__panel';
    panel.setAttribute('role', 'listbox');
    panel.tabIndex = -1;
    if (ariaLabel) panel.setAttribute('aria-label', ariaLabel);
    else if (label && label.id) panel.setAttribute('aria-labelledby', label.id);

    var options = Array.prototype.slice.call(select.options);
    var optionEls = options.map(function (opt, i) {
      var li = document.createElement('li');
      li.className = 'frido-b2b-combo__option';
      li.id = baseId + '-opt-' + i;
      li.setAttribute('role', 'option');
      li.textContent = opt.textContent;
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      panel.appendChild(li);
      return li;
    });

    combo.appendChild(button);
    combo.appendChild(panel);

    select.classList.add('frido-b2b-form__select--native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    select.parentNode.insertBefore(combo, select.nextSibling);

    var activeIndex = select.selectedIndex < 0 ? 0 : select.selectedIndex;
    var isOpen = false;

    function syncValue() {
      var opt = select.options[select.selectedIndex] || select.options[0];
      valueSpan.textContent = opt ? opt.textContent : '';
      optionEls.forEach(function (li, i) {
        li.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
      });
    }

    function setActive(i) {
      activeIndex = Math.max(0, Math.min(optionEls.length - 1, i));
      optionEls.forEach(function (li, idx) {
        li.classList.toggle('frido-b2b-combo__option--active', idx === activeIndex);
      });
      button.setAttribute('aria-activedescendant', optionEls[activeIndex].id);

      var li = optionEls[activeIndex];
      if (li.offsetTop < panel.scrollTop) {
        panel.scrollTop = li.offsetTop;
      } else if (li.offsetTop + li.offsetHeight > panel.scrollTop + panel.clientHeight) {
        panel.scrollTop = li.offsetTop + li.offsetHeight - panel.clientHeight;
      }
    }

    function onDocPointer(event) {
      if (!combo.contains(event.target)) close(false);
    }

    /* The panel is position:fixed so it escapes the phone row's overflow:hidden
       and any other clipping ancestor. That means we place it from the button's
       viewport rect and keep it pinned while open. */
    function positionPanel() {
      var rect = button.getBoundingClientRect();
      var spaceBelow = window.innerHeight - rect.bottom;
      var need = Math.min(260, panel.offsetHeight + 16);
      var up = spaceBelow < need && rect.top > spaceBelow;

      combo.classList.toggle('frido-b2b-combo--up', up);
      panel.style.left = rect.left + 'px';
      panel.style.minWidth = rect.width + 'px';
      if (up) {
        panel.style.top = 'auto';
        panel.style.bottom = (window.innerHeight - rect.top + 8) + 'px';
      } else {
        panel.style.bottom = 'auto';
        panel.style.top = (rect.bottom + 8) + 'px';
      }
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      combo.classList.add('frido-b2b-combo--open');
      button.setAttribute('aria-expanded', 'true');
      positionPanel();
      setActive(select.selectedIndex < 0 ? 0 : select.selectedIndex);
      document.addEventListener('pointerdown', onDocPointer, true);
      window.addEventListener('scroll', positionPanel, true);
      window.addEventListener('resize', positionPanel);
    }

    function close(focusButton) {
      if (!isOpen) return;
      isOpen = false;
      combo.classList.remove('frido-b2b-combo--open');
      button.setAttribute('aria-expanded', 'false');
      button.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', onDocPointer, true);
      window.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
      if (focusButton) button.focus();
    }

    function choose(i) {
      if (i !== select.selectedIndex) {
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      syncValue();
      close(true);
    }

    button.addEventListener('click', function () {
      if (isOpen) close(true); else open();
    });

    button.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (isOpen) setActive(activeIndex + 1); else open();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) setActive(activeIndex - 1); else open();
          break;
        case 'Home':
          if (isOpen) { event.preventDefault(); setActive(0); }
          break;
        case 'End':
          if (isOpen) { event.preventDefault(); setActive(optionEls.length - 1); }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen) choose(activeIndex); else open();
          break;
        case 'Escape':
          if (isOpen) { event.preventDefault(); close(true); }
          break;
        case 'Tab':
          close(false);
          break;
        default:
          if (event.key.length === 1) {
            var ch = event.key.toLowerCase();
            for (var k = 1; k <= optionEls.length; k++) {
              var idx = (activeIndex + k) % optionEls.length;
              if (optionEls[idx].textContent.trim().toLowerCase().charAt(0) === ch) {
                if (!isOpen) open();
                setActive(idx);
                break;
              }
            }
          }
      }
    });

    panel.addEventListener('click', function (event) {
      var li = event.target.closest('.frido-b2b-combo__option');
      if (li) choose(optionEls.indexOf(li));
    });

    panel.addEventListener('pointermove', function (event) {
      var li = event.target.closest('.frido-b2b-combo__option');
      if (li) {
        activeIndex = optionEls.indexOf(li);
        optionEls.forEach(function (node, idx) {
          node.classList.toggle('frido-b2b-combo__option--active', idx === activeIndex);
        });
      }
    });

    /* Re-sync if the value is changed elsewhere (e.g. reset). */
    select.addEventListener('change', syncValue);

    syncValue();
  }

  /* ---------------------------------------------------------------------------
   * OTPless phone verification (optional — only when a form carries an App ID).
   * The SDK plumbing lives in the reusable FridoOtpless helper; this just wires
   * it to the form's fields and gates submission on the result.
   * ------------------------------------------------------------------------- */

  function initOtpless(form) {
    var appId = form.dataset.otplessAppid;
    if (!appId || !window.FridoOtpless) return;

    var phoneInput = form.elements.phone;
    var codeSelect = form.elements.country_code;
    var tokenField = form.elements.otpless_token;
    var verifiedField = form.elements.phone_verified;

    var sendBtn = form.querySelector('[data-otp-send]');
    var verifiedTag = form.querySelector('[data-otp-verified]');
    var otpBox = form.querySelector('[data-otp-box]');
    var otpInput = form.querySelector('[data-otp-input]');
    var confirmBtn = form.querySelector('[data-otp-confirm]');
    var resendBtn = form.querySelector('[data-otp-resend]');
    var otpStatus = form.querySelector('[data-otp-status]');

    if (!phoneInput || !sendBtn) return;

    function currentPhone() { return phoneInput.value.replace(/\D/g, ''); }
    function currentCode() { return codeSelect ? codeSelect.value : '+91'; }

    function setOtpStatus(message, kind) {
      if (!otpStatus) return;
      otpStatus.textContent = message || '';
      otpStatus.className = 'frido-b2b-form__otp-status'
        + (kind ? ' frido-b2b-form__otp-status--' + kind : '');
    }

    function otpDigits() {
      return otpInput ? otpInput.value.replace(/\D/g, '') : '';
    }

    function otpEntered() {
      return otpDigits().length === OTP_LENGTH;
    }

    /* Confirm is usable only once a full OTP is typed (unless a verify is
       running). */
    function syncConfirm(verifying) {
      if (confirmBtn) confirmBtn.disabled = Boolean(verifying) || !otpEntered();
    }

    /* Ends the "Verifying…" state from whichever path finishes first — the SDK
       event, the watchdog, or a rejected verify() — and makes sure a later
       arrival can't re-open it. */
    var verifyTimer = null;

    function stopVerifying() {
      if (verifyTimer) {
        window.clearTimeout(verifyTimer);
        verifyTimer = null;
      }
      syncConfirm(false);
    }

    function toggleSendButton() {
      if (verifiedField && verifiedField.value === 'true') return;
      var digits = currentPhone();
      sendBtn.hidden = !(digits.length >= 7 && digits.length <= 15);
    }

    function revealOtpBox() {
      if (otpBox) otpBox.hidden = false;
      if (otpInput) otpInput.focus();
    }

    var otp = window.FridoOtpless.create(appId, {
      /* INITIATE — not relied on for the UI (it doesn't fire reliably); the box
         is already revealed on click. This just refreshes the status if it does. */
      onSent: function () {
        setOtpStatus('OTP sent to ' + currentCode() + ' ' + currentPhone(), 'ok');
      },
      onAutoRead: function (code) {
        if (otpInput) otpInput.value = code;
        syncConfirm(false);
      },
      onVerified: function (token) {
        stopVerifying();
        if (tokenField) tokenField.value = token || 'verified';
        if (verifiedField) verifiedField.value = 'true';
        if (otpBox) otpBox.hidden = true;
        sendBtn.hidden = true;
        if (verifiedTag) verifiedTag.hidden = false;
        phoneInput.readOnly = true;
        setFieldError(phoneInput, '');
        setOtpStatus('', null);
      },
      onError: function (message) {
        stopVerifying();
        setOtpStatus(message, 'error');
        if (sendBtn) sendBtn.disabled = false;
        if (resendBtn) resendBtn.disabled = false;
        /* Let them correct it straight away: select the bad code so typing
           replaces it rather than appending to six already-wrong digits. */
        if (otpInput && otpDigits().length) {
          otpInput.focus();
          otpInput.select();
        }
      }
    });

    function sendOtp() {
      var digits = currentPhone();
      if (digits.length < 7) {
        setFieldError(phoneInput, 'Please enter a valid mobile number');
        return;
      }
      /* A resend supersedes any verify still in flight. */
      stopVerifying();

      /* Reveal the OTP input on click rather than waiting for the SDK's
         INITIATE callback, which doesn't reliably fire. */
      revealOtpBox();
      if (sendBtn) sendBtn.disabled = true;
      if (resendBtn) resendBtn.disabled = true;
      setOtpStatus('Sending OTP…', null);

      /* The promise resolves once initiate() is dispatched — the delivery result
         still arrives via callback, but this is enough to re-enable the buttons
         and confirm the request went out, independent of the SDK's events. */
      otp.sendOtp(digits, currentCode()).then(function () {
        setOtpStatus('OTP sent to ' + currentCode() + ' ' + digits, 'ok');
      }).catch(function () {
        /* onError has already shown a message. */
      }).then(function () {
        if (sendBtn) sendBtn.disabled = false;
        if (resendBtn) resendBtn.disabled = false;
      });
    }

    function confirmOtp() {
      var raw = otpInput ? otpInput.value.trim() : '';
      var code = raw.replace(/\D/g, '');

      /* Say precisely what's wrong — "invalid OTP" for a typo, a short code and
         a wrong code are three different fixes. */
      if (!raw) {
        setOtpStatus('Enter the ' + OTP_LENGTH + '-digit OTP you received.', 'error');
        return;
      }
      if (raw.length !== code.length) {
        setOtpStatus('The OTP is numbers only — remove any letters or spaces.', 'error');
        return;
      }
      if (code.length !== OTP_LENGTH) {
        setOtpStatus('The OTP is ' + OTP_LENGTH + ' digits — you entered ' + code.length + '.', 'error');
        return;
      }

      syncConfirm(true);
      setOtpStatus('Verifying…', null);

      /* verify() only reports back through the event callback, so nothing here
         resolves on failure. The watchdog is what guarantees the UI comes back
         if that event is dropped, delayed, or a shape we don't recognise. */
      verifyTimer = window.setTimeout(function () {
        verifyTimer = null;
        syncConfirm(false);
        setOtpStatus('That took too long. Check the OTP and try again, or resend it.', 'error');
      }, OTP_VERIFY_TIMEOUT);

      otp.verifyOtp(currentPhone(), code, currentCode()).catch(function () {
        /* onError has already shown a message and cleared the timer. */
        stopVerifying();
      });
    }

    /* Changing the number after verifying invalidates it. */
    function resetVerification() {
      if (verifiedField && verifiedField.value === 'true') {
        if (tokenField) tokenField.value = '';
        verifiedField.value = 'false';
        if (verifiedTag) verifiedTag.hidden = true;
        phoneInput.readOnly = false;
      }
      toggleSendButton();
    }

    sendBtn.addEventListener('click', sendOtp);
    if (confirmBtn) confirmBtn.addEventListener('click', confirmOtp);
    if (resendBtn) resendBtn.addEventListener('click', sendOtp);
    phoneInput.addEventListener('input', resetVerification);
    if (codeSelect) codeSelect.addEventListener('change', resetVerification);
    if (otpInput) {
      /* Keep the field to digits as they type — pasting an OTP out of an SMS
         often drags in spaces or a stray letter, and silently dropping those is
         friendlier than rejecting the paste on Confirm. */
      otpInput.setAttribute('maxlength', String(OTP_LENGTH));
      otpInput.addEventListener('input', function () {
        var raw = otpInput.value;
        var hadLetters = /\D/.test(raw);
        var digits = raw.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (digits !== raw) otpInput.value = digits;
        stopVerifying();

        /* Confirm stays disabled until all six digits are in, so without this
           the field is a silent dead end — the button just never lights up and
           nothing says why. */
        if (hadLetters) {
          setOtpStatus('The OTP is numbers only.', 'error');
        } else if (digits.length && digits.length < OTP_LENGTH) {
          var left = OTP_LENGTH - digits.length;
          setOtpStatus(left + ' more digit' + (left === 1 ? '' : 's') + ' to go.', null);
        } else {
          setOtpStatus('', null);
        }
      });
    }

    syncConfirm(false); // Confirm starts disabled until an OTP is typed
    toggleSendButton();
    /* Submission is gated inside the main submit handler (see initForm). */
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-b2b-form]').forEach(initForm);
  }

  /* Any [data-b2b-scroll="#selector"] element (e.g. the Get In Touch CTA button)
     smooth-scrolls to that form and focuses its first field — same UX as the
     product drawer's quote button. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-b2b-scroll]');
    if (!trigger) return;

    var selector = trigger.getAttribute('data-b2b-scroll') || trigger.getAttribute('href');
    if (!selector || selector.charAt(0) !== '#') return;

    var form = document.querySelector(selector);
    if (!form) return;

    event.preventDefault();
    form.scrollIntoView({ behavior: 'smooth', block: 'center' });

    var firstField = form.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
    if (firstField) {
      window.setTimeout(function () { firstField.focus({ preventScroll: true }); }, 500);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
